//#region Imports Packages and Libs

import { BaseService } from "../base_service";
import { HttpRequest } from "../../helpers/http_request";
import {
  SaveFeedbackResponse,
  SaveFeedbackError,
  SaveFeedbackParams,
  FeedbackErrorName,
  FeedbackErrorNames,
} from "../../types/chat/feedback";
import { store } from "../../store/store";
import { ChatService } from "../../service/chat_service";

//#endregion

//#region Define the Feedback Service Class

/**
 * The `FeedbackService` class handles all API calls related to message feedback functionality.
 * It extends BaseService to inherit common functionality like appUrl access.
 */
export class FeedbackService extends BaseService {
  //#region Private Helper Methods

  /**
   * Validates the parameters for feedback API calls.
   * @param params - The parameters to validate.
   * @returns Validation error message or null if valid.
   */
  private validateSaveFeedbackParams(params: SaveFeedbackParams): string | null {
    try {
      // Check required fields
      if (
        params.ExtBotId === undefined ||
        params.ExtBotId === null ||
        typeof params.ExtBotId !== "number" ||
        Number.isNaN(params.ExtBotId) ||
        !Number.isFinite(params.ExtBotId)
      ) {
        return "[feedback_service.ts] ExtBotId is required and must be a valid number";
      }

      // Validate ExtBotId is positive (assuming bot IDs are positive integers)
      if (params.ExtBotId < 0) {
        return "[feedback_service.ts] ExtBotId must be a positive number";
      }

      if (!params.ExtMsgId || typeof params.ExtMsgId !== "string" || params.ExtMsgId.trim() === "") {
        return "[feedback_service.ts] ExtMsgId is required and must be a non-empty string";
      }

      if (!params.Key || typeof params.Key !== "string" || params.Key.trim() === "") {
        return "[feedback_service.ts] Key is required and must be a non-empty string";
      }

      // Validate optional Value field if provided
      if (params.Value !== undefined && typeof params.Value !== "string") {
        return "[feedback_service.ts] Value must be a string if provided";
      }

      return null; // Valid
    } catch (ex) {
      return `[feedback_service.ts] Parameter validation failed: \n${ex}`;
    }
  }

  /**
   * Processes the API response and validates its structure.
   * @param response - Raw API response.
   * @returns Processed SaveFeedbackResponse.
   */
  private processApiResponse(response: any): SaveFeedbackResponse {
    try {
      // Handle different response formats
      let processedResponse: SaveFeedbackResponse = response;

      // Validate response structure
      if (!processedResponse || typeof processedResponse !== "object") {
        throw new Error("Invalid response format: expected object");
      }

      // Check for status property
      if (typeof processedResponse.status !== "boolean") {
        throw new Error("Invalid response: missing or invalid status property");
      }

      return {
        status: processedResponse?.status,
        statusMessage: processedResponse?.statusMessage ?? "",
      };
    } catch (ex) {
      console.error("[feedback_service.ts] Error processing API response:", ex);
      return this.createErrorResponse(
        "FEEDBACK_RESPONSE_ERROR",
        "Something went wrong while processing the API response",
        ex
      );
    }
  }

  /**
   * Creates a standardized error response.
   * @param errorName - The type of error.
   * @param message - Human-readable error message.
   * @param cause - Original error details.
   * @returns Standardized SaveFeedbackResponse with error details.
   */
  private createErrorResponse(errorName: FeedbackErrorName, message: string, cause?: any): SaveFeedbackResponse {
    const error: SaveFeedbackError = {
      name: errorName,
      message,
      cause,
    };

    console.error(`[feedback_service.ts] ${errorName}:`, error);

    return {
      status: false,
      statusMessage: message,
    };
  }

  //#endregion

  //#region Public API Methods

  /**
   * Saves a user's rating (thumbs up or down) for a message
   *
   * @param params - The parameters required for saving feedback
   * @returns A promise that resolves to a SaveFeedbackResponse object
   */
  async saveMessageRating(params: SaveFeedbackParams): Promise<SaveFeedbackResponse> {
    try {
      // Validate parameters
      const validationError = this.validateSaveFeedbackParams(params);
      if (validationError) {
        return this.createErrorResponse(
          FeedbackErrorNames.SendRatingValidationError,
          `Message rating not saved. Required fields are missing or invalid.`,
          {
            params,
            validationError,
          }
        );
      }

      // Send request to API
      const request = new HttpRequest(`${this.appUrl}Default/SaveUserMessageFeedback`);
      const rawResponse = await request.sendPost(params);

      try{
        if (params.Value === "1" && store?.state?.thumbsUpResponse?.trim()) {
          const service = new ChatService();
          service.sendMessage({
                    content: store.state.thumbsUpResponse,
                    type: "text"
                });
        }
      }
      catch(ex){
        console.log(ex);
      }
      // Process and validate response
      const response = this.processApiResponse(rawResponse);
      return response;
    } catch (ex) {
      return this.createErrorResponse(FeedbackErrorNames.SendMessageRatingError, "Failed to send message rating", ex);
    }
  }

  /**
   * Saves user feedback for a message to the server, for a thumbs down rating.
   *
   * @param params - The parameters required to save feedback
   * @returns A promise that resolves to a SaveFeedbackResponse object
   */
  async saveMessageFeedback(params: SaveFeedbackParams): Promise<SaveFeedbackResponse> {
    try {
      // Validate parameters
      const validationError = this.validateSaveFeedbackParams(params);
      if (validationError) {
        return this.createErrorResponse(
          FeedbackErrorNames.SendFeedbackValidationError,
          `Message feedback not saved. Required fields are missing or invalid.`,
          { params, validationError }
        );
      }

      // Send request to API
      const request = new HttpRequest(`${this.appUrl}Default/SaveUserMessageFeedback`);
      const rawResponse = await request.sendPost(params);

      // Process and validate response
      const response = this.processApiResponse(rawResponse);
      return response;
    } catch (ex) {
      return this.createErrorResponse(
        FeedbackErrorNames.SendMessageFeedbackError,
        "Failed to send message feedback",
        ex
      );
    }
  }

  //#endregion
}

//#endregion
